import { CommonModule } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	HostBinding,
	HostListener,
	ViewChild,
	computed,
	inject,
	input,
	output,
	signal
} from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { XpButtonComponent, XpButtonVariant } from '@xp/button/xp-button.component';

export type XpDropZoneProgressMode = 'determinate' | 'indeterminate';

@Component({
	selector: 'xp-drop-zone',
	standalone: true,
	imports: [CommonModule, MatProgressBarModule, XpButtonComponent],
	templateUrl: './xp-drop-zone.component.html',
	styleUrls: ['./xp-drop-zone.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class XpDropZoneComponent {
	hint = input('');
	dragText = input('');
	browseLabel = input('');
	browseVariant = input<XpButtonVariant>('solid');
	accept = input('');
	multiple = input(false);
	disabled = input(false);
	showError = input(false);
	errorIconSrc = input('');
	errorTitle = input('');
	errorLabel = input('');
	retryLabel = input('');
	showProgress = input(false);
	progress = input(0);
	progressMode = input<XpDropZoneProgressMode>('indeterminate');
	cancelLabel = input('');

	filesSelected = output<File[]>();
	cancel = output<void>();

	readonly dragging = signal(false);

	readonly message = computed(() => {
		if (this.showProgress()) {
			return '';
		}
		if (this.dragging()) {
			return this.dragText();
		}
		return this.showError() ? '' : this.hint();
	});

	readonly errorVisible = computed(() => this.showError() && !this.dragging() && !this.showProgress());

	readonly browseText = computed(() => (this.showError() ? this.retryLabel() : this.browseLabel()));

	private readonly takesFiles = computed(() => !this.disabled() && !this.showProgress());

	@ViewChild('fileInput') private fileInput?: ElementRef<HTMLInputElement>;

	private readonly host: HTMLElement = inject(ElementRef).nativeElement;

	private lastEntered: EventTarget | null = null;

	@HostBinding('class.xp-drop-zone--dragging')
	get draggingClass(): boolean {
		return this.dragging();
	}

	browse(): void {
		this.fileInput?.nativeElement.click();
	}

	onFilePicked(event: Event): void {
		const input = event.target as HTMLInputElement;
		const files = Array.from(input.files ?? []);
		// Clearing lets the same file be picked again and fire another change event.
		input.value = '';
		this.emit(files);
	}

	@HostListener('dragenter', ['$event'])
	onDragEnter(event: DragEvent): void {
		this.lastEntered = event.target;
	}

	@HostListener('dragover', ['$event'])
	onDragOver(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		if (this.takesFiles()) {
			this.dragging.set(true);
		}
	}

	@HostListener('dragleave', ['$event'])
	onDragLeave(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		// Safari sends dragleave with a null relatedTarget; every browser fires dragenter on the next element first.
		const next = event.relatedTarget ?? this.lastEntered;
		if (next instanceof Node && next !== event.target && this.host.contains(next)) {
			return;
		}
		this.dragging.set(false);
	}

	@HostListener('drop', ['$event'])
	onDrop(event: DragEvent): void {
		event.preventDefault();
		event.stopPropagation();
		this.dragging.set(false);
		if (!this.takesFiles()) {
			return;
		}
		this.emit(Array.from(event.dataTransfer?.files ?? []));
	}

	private emit(files: File[]): void {
		// A single-file zone matches the hidden input: a dropped group keeps the first file.
		const accepted = this.multiple() ? files : files.slice(0, 1);
		if (accepted.length > 0) {
			this.filesSelected.emit(accepted);
		}
	}
}
